import React from "react";
import { Pin, Code2, ExternalLink } from "lucide-react";
import { SITE } from "@/constants/testIds";

/**
 * Reusable GHL embed placeholder.
 *
 * IMPORTANT: This component intentionally renders a clearly-marked, styled
 * placeholder for the site owner to replace with their GHL embed snippet
 * (iframe/script). Do NOT recreate GHL form fields in React.
 *
 * Usage:
 *   <GhlEmbedPlaceholder kind="hero-nav" />
 *   <GhlEmbedPlaceholder kind="consultation-form" />
 *   <GhlEmbedPlaceholder kind="map" />
 */
export const GhlEmbedPlaceholder = ({
    kind = "consultation-form",
    title,
    description,
    height,
    className = "",
}) => {
    const meta = {
        "hero-nav": {
            title: "Global Hero + Navigation — GHL Embed",
            description:
                "Replace this block with the GHL Hero + Nav embed snippet you already use on the live site.",
            tag: "PASTE HERE",
            minH: "min-h-[160px]",
        },
        "consultation-form": {
            title: "Consultation Request Form — GHL Embed",
            description:
                "Replace this block with the GHL form embed (iframe or script) for consultation requests.",
            tag: "PASTE HERE",
            minH: "min-h-[420px]",
        },
        map: {
            title: "Location Map — Embed",
            description:
                "Paste your Google Maps iframe for 3660 Baldwin Ave. Suite C, Makawao, HI 96708.",
            tag: "PASTE HERE",
            minH: "min-h-[260px]",
        },
    }[kind] || {
        title: "Embed Placeholder",
        description: "Replace this block with your embed snippet.",
        tag: "PASTE HERE",
        minH: "min-h-[200px]",
    };

    const finalTitle = title || meta.title;
    const finalDesc = description || meta.description;

    return (
        <div
            data-testid={SITE.ghlEmbedWrapper}
            data-embed-kind={kind}
            className={`relative rounded-2xl border border-terracotta/40 bg-sand/60 p-5 sm:p-7 ${className}`}
        >
            {/* HTML comment for the developer */}
            {/* GHL EMBED PLACEHOLDER: paste the provided GHL embed snippet below this line */}
            <span
                data-testid={SITE.ghlEmbedLabel}
                className="absolute -top-3 left-5 inline-flex items-center gap-1.5 rounded-full bg-terracotta px-3 py-1 text-[0.7rem] font-semibold uppercase tracking-wider text-cream shadow-sm"
            >
                <Pin className="h-3 w-3" strokeWidth={2} />
                {meta.tag}
            </span>

            <div className="flex items-start gap-3">
                <Code2 className="mt-0.5 h-5 w-5 shrink-0 text-moss" strokeWidth={1.75} />
                <div className="flex-1">
                    <p className="font-display text-lg text-moss">{finalTitle}</p>
                    <p className="mt-1 max-w-prose text-sm text-foreground/70">
                        {finalDesc}
                    </p>
                </div>
            </div>

            <div
                data-testid={SITE.ghlEmbedPasteArea}
                className={`mt-4 rounded-xl border border-dashed border-moss/30 bg-cream/80 p-4 font-mono text-xs text-foreground/60 ${meta.minH} ${height || ""}`}
                style={height ? { minHeight: height } : undefined}
            >
                &lt;!-- GHL EMBED ({kind}): paste the GHL snippet (iframe / script) here. --&gt;
                <br />
                &lt;!-- The wrapping <code>div</code> above keeps your embed looking consistent with the rest of the site. --&gt;
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-3 text-[0.7rem] uppercase tracking-wider text-foreground/50">
                <span className="font-accent">DEV NOTE</span>
                <span className="text-foreground/60 normal-case tracking-normal font-body text-xs">
                    Remove this placeholder file or this block once your GHL snippet is pasted.
                </span>
                <a
                    href="https://help.gohighlevel.com/support/solutions/articles/48000981146-embedding-a-form"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-moss hover:text-terracotta normal-case tracking-normal font-body text-xs"
                >
                    GHL embed help <ExternalLink className="h-3 w-3" />
                </a>
            </div>
        </div>
    );
};

export default GhlEmbedPlaceholder;
